import type {
  BaseServiceOptions,
  Pagination,
  THistoryActions,
  TRecordAction,
} from '@/core/types'
import {
  type TReception,
  moduleServiceHandlers,
  serviceConfig,
  type TReceptions,
  TReceptionDetail,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'

const { model } = serviceConfig

export const useModuleQuery = (
  options: BaseServiceOptions<Pagination, TReception>,
) => {
  const { currentPage, modelId, handlers, enableQuery, filters } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TReception>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const editRecord = useMutation<TReception, AxiosError, TReception>(
    (payload: TReception) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TReception, AxiosError, TReception>(
    (payload: TReception) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TReception, AxiosError, TRecordAction>(
    (payload: TRecordAction) =>
      moduleServiceHandlers.actionRecord({
        ...payload,
        data: payload.data ? transformReceptionDetails(payload.data) : {},
      }),
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TReceptions, AxiosError>(
    [model, currentPage, filters?.loadRecords],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (data) => {
        if (data.meta) {
          handlers?.onDataChange?.(data.meta ?? defaultPagination)
        }
      },
    },
  )

  const loadRecord = useQuery<TReception, AxiosError>({
    queryKey: [model, modelId],
    queryFn: ({ queryKey }) =>
      moduleServiceHandlers.loadRecord({
        id: Number(queryKey[1]),
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data.id) {
        handlers?.onLoadRecord?.(data)
      }
    },
  })

  const loadHistoryActions = useQuery<THistoryActions[], AxiosError>({
    queryKey: [model, modelId],
    queryFn: ({ queryKey }) => {
      return moduleServiceHandlers.loadHistoryActions({
        id: queryKey[1] as number,
      })
    },
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadHistoryActions,
    onError: (error) => errorHttpNotification(error),
  })

  const transformReceptionDetails = (data: TReceptionDetail[]) => ({
    details: data.map((record: TReceptionDetail) => ({
      id: record?.id,
      to_receive: record?.to_receive,
    })),
  })

  return {
    loadRecord,
    loadRecords,
    actionRecord,
    editRecord,
    addRecord,
    loadHistoryActions,
  }
}
